import React, { useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Input } from '../../../components/ui/Input';
import { Select } from '../../../components/ui/Select';
import { Button, IconButton } from '../../../components/ui/Button';
import MarkdownEditor from './MarkdownEditor';
import { SUPPORTED_LANGUAGES } from '../constants';
import { cn } from '../../../lib/utils';

/**
 * Post Translation Tabs Component
 * Each tab represents a language translation
 */
function PostTranslationTabs({ translations, onChange }) {
  const [activeTab, setActiveTab] = useState(0);

  const handleTranslationChange = (index, field, value) => {
    const newTranslations = [...translations];
    newTranslations[index] = {
      ...newTranslations[index],
      [field]: value
    };
    onChange(newTranslations);
  };

  const handleLanguageChange = (index, languageCode) => {
    const newTranslations = [...translations];
    newTranslations[index] = {
      ...newTranslations[index],
      language_code: languageCode
    };
    onChange(newTranslations);
  };

  const handleAddTranslation = () => {
    const newTranslation = {
      language_code: 'tr',
      title: '',
      content: '',
      excerpt: ''
    };
    onChange([...translations, newTranslation]);
    setActiveTab(translations.length);
  };

  const handleRemoveTranslation = (e, index) => {
    e.stopPropagation();
    if (translations.length <= 1) {
      return; // En az bir çeviri olmalı
    }
    const newTranslations = translations.filter((_, i) => i !== index);
    onChange(newTranslations);
    if (activeTab >= newTranslations.length) {
      setActiveTab(Math.max(0, newTranslations.length - 1));
    }
  };

  const currentTranslation = translations[activeTab] || translations[0];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between border-b border-zinc-800 pb-4">
        <h3 className="text-lg font-semibold text-zinc-100">Çeviriler</h3>
        <Button
          onClick={handleAddTranslation}
          size="sm"
          className="gap-2"
        >
          <Plus className="w-4 h-4" />
          Çeviri Ekle
        </Button>
      </div>

      <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
        {translations.map((translation, index) => (
          <button
            key={index}
            onClick={() => setActiveTab(index)}
            className={cn(
              "px-4 py-2 rounded-md transition-all flex items-center gap-2 whitespace-nowrap",
              activeTab === index
                ? "bg-zinc-800 text-zinc-100 border border-zinc-700"
                : "bg-transparent text-zinc-400 hover:text-zinc-200 border border-transparent"
            )}
            type="button"
          >
            {translation.language_code.toUpperCase()}
            {translations.length > 1 && (
              <IconButton
                size="iconSm"
                onClick={(e) => handleRemoveTranslation(e, index)}
                variant="ghost"
                icon={Trash2}
                className="hover:text-red-500 -mr-2"
              />
            )}
          </button>
        ))}
      </div>

      {currentTranslation && (
        <div className="p-6 rounded-lg bg-zinc-900/50 border border-zinc-800 space-y-6">
          <div className="grid gap-6">
            <div className="space-y-1.5">
              <label className="text-xs text-gray-500 uppercase font-semibold">Dil Seçimi</label>
              <Select
                value={currentTranslation.language_code}
                onChange={(e) => handleLanguageChange(activeTab, e.target.value)}
              >
                {SUPPORTED_LANGUAGES.map(lang => (
                  <option key={lang} value={lang}>
                    {lang.toUpperCase()}
                  </option>
                ))}
              </Select>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs text-gray-500 uppercase font-semibold">Başlık</label>
              <Input
                value={currentTranslation.title || ''}
                onChange={(e) => handleTranslationChange(activeTab, 'title', e.target.value)}
                placeholder="Başlık giriniz..."
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-xs text-gray-500 uppercase font-semibold">Özet</label>
              <MarkdownEditor
                value={currentTranslation.excerpt || ''}
                onChange={(val) => handleTranslationChange(activeTab, 'excerpt', val || '')}
                height={200}
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-xs text-gray-500 uppercase font-semibold">İçerik</label>
              <MarkdownEditor
                value={currentTranslation.content || ''}
                onChange={(val) => handleTranslationChange(activeTab, 'content', val || '')}
                height={500}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PostTranslationTabs;

